import { Flex, Input, IconButton, useColorModeValue, Button, Menu, MenuButton, MenuList, MenuItem, Avatar, AvatarGroup, Tooltip, Box, Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverBody, PopoverArrow, PopoverCloseButton, Badge, Text, Heading, Spinner, Stack, Divider, useToast, useDisclosure, useColorMode, Portal, HStack, VStack, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Textarea, FormControl, FormLabel, SimpleGrid } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon, faBell, faComments, faUsers, faVideo, faSave, faTimes, faQuestionCircle, faBolt, faFolderPlus, faBug, faCheck, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import TopNavChat from "./TopNavChat";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import { fetchUserAttributes, FetchUserAttributesOutput } from "@aws-amplify/auth";
import { apiGetRequest, apiPutRequest, apiDeleteRequest, apiPostRequest } from "@/functions/requests";
import { AppMeta } from "@xnav/types/src/common";
import { createTicket } from "@/functions/ticketService";

interface TopNavProps {
	username?: string;
}

export default function TopNav({ username }: TopNavProps) {
	const { colorMode, toggleColorMode } = useColorMode();
	const bg = useColorModeValue("white", "gray.800");
	const popoverBorder = useColorModeValue("purple.50", "gray.750");
	const popoverBg = useColorModeValue("white", "gray.800");
	const popoverHeaderBorder = useColorModeValue("gray.100", "gray.700");
	const dividerColor = useColorModeValue("gray.100", "gray.700");
	const itemHoverBg = useColorModeValue("gray.50", "gray.750/40");
	const textTitleColor = useColorModeValue("gray.850", "white");
	const inlineReasonBg = useColorModeValue("gray.50", "gray.900");
	const inlineInputBg = useColorModeValue("white", "gray.800");

	const navigate = useNavigate();
	const { appId } = useParams() as { appId?: string };
	const { pathname } = useLocation();
	const [currentApp, setCurrentApp] = useState<AppMeta | null>(null);
	const [selectionPath, setSelectionPath] = useState<string[]>([]);
	const [isDirty, setIsDirty] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [showUnsavedModal, setShowUnsavedModal] = useState(false);
	const [pendingNavPath, setPendingNavPath] = useState<string | null>(null);

	const handleSafeNavigate = (path: string) => {
		if (isDirty) {
			setPendingNavPath(path);
			setShowUnsavedModal(true);
		} else {
			navigate(path);
		}
	};

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (isDirty) {
				e.preventDefault();
				e.returnValue = '';
				return '';
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [isDirty]);

	useEffect(() => {
		const handleDirtyState = (e: Event) => {
			const customEvent = e as CustomEvent;
			setIsDirty(customEvent.detail.isDirty);
		};
		window.addEventListener('editor-dirty-state', handleDirtyState);
		return () => {
			window.removeEventListener('editor-dirty-state', handleDirtyState);
		};
	}, []);

	useEffect(() => {
		const handleSavingState = (e: Event) => {
			const customEvent = e as CustomEvent<{ isSaving: boolean }>;
			setIsSaving(!!customEvent.detail?.isSaving);
		};
		window.addEventListener('editor-saving-state', handleSavingState);
		return () => {
			window.removeEventListener('editor-saving-state', handleSavingState);
		};
	}, []);

	useEffect(() => {
		const handleSelectionChanged = (e: Event) => {
			const customEvent = e as CustomEvent<{ path: string[] }>;
			setSelectionPath(customEvent.detail.path || []);
		};
		window.addEventListener('editor-selection-changed', handleSelectionChanged);
		return () => {
			window.removeEventListener('editor-selection-changed', handleSelectionChanged);
		};
	}, []);

	useEffect(() => {
		const fetchApp = async () => {
			if (appId) {
				try {
					const data = await apiGetRequest<string, AppMeta>(`/apps/${appId}`);
					setCurrentApp(data);
				} catch (e) {
					console.error("Failed to fetch app for topnav", e);
				}
			} else {
				setCurrentApp(null);
			}
		};
		fetchApp();
	}, [appId]);

	const { signOut, user } = useAuthenticator();
	const toast = useToast();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isChatOpen, onOpen: onChatOpen, onClose: onChatClose } = useDisclosure();
	const { isOpen: isRecentChatsOpen, onOpen: onRecentChatsOpen, onClose: onRecentChatsClose } = useDisclosure();
	const [globalUnreadCount, setGlobalUnreadCount] = useState(0);
	const [chatSummaries, setChatSummaries] = useState<any[]>([]);
	const [selectedChatId, setSelectedChatId] = useState<string>('');
	const [selectedChatTitle, setSelectedChatTitle] = useState<string>('');

	const parseChatId = (chatId: string, currentUserEmail: string) => {
		const parts = chatId.split('#');
		const org = parts[0] || 'System';
		const displayOrg = (org.toLowerCase() === 'global' || org.toLowerCase() === 'system') ? 'XNav' : org;
		if (parts[1] === 'broadcast') {
			return {
				title: `${displayOrg} Broadcast`,
				subtitle: "Broadcast Channel",
				isBroadcast: true
			};
		} else if (parts[1] === 'direct') {
			const email1 = parts[2] || '';
			const email2 = parts[3] || '';
			const otherEmail = email1.toLowerCase() === currentUserEmail.toLowerCase() ? email2 : email1;
			const cleanName = otherEmail.split('@')[0] || otherEmail;
			return {
				title: cleanName,
				subtitle: `Direct Message (${displayOrg})`,
				isBroadcast: false,
				otherEmail
			};
		}
		return { title: chatId, subtitle: "", isBroadcast: false };
	};

	const fetchGlobalUnreadCount = async () => {
		if (!user?.signInDetails?.loginId) return;
		try {
			const data = await apiGetRequest<undefined, { chatId: string; lastReadTimestamp: number; latestMessageTimestamp: number }[]>('/chats/read');
			if (data) {
				const map = new Map<string, { chatId: string; lastReadTimestamp: number; latestMessageTimestamp: number }>();
				data.forEach(s => {
					let cid = s.chatId;
					const parts = cid.split('#');
					if (parts.length >= 4 && parts[1] === 'direct') {
						const sorted = [parts[2].toLowerCase().trim(), parts[3].toLowerCase().trim()].sort();
						cid = `Global#direct#${sorted[0]}#${sorted[1]}`;
					}
					const existing = map.get(cid);
					if (!existing || s.latestMessageTimestamp > existing.latestMessageTimestamp) {
						map.set(cid, {
							chatId: cid,
							lastReadTimestamp: existing ? Math.max(existing.lastReadTimestamp, s.lastReadTimestamp) : s.lastReadTimestamp,
							latestMessageTimestamp: Math.max(existing?.latestMessageTimestamp || 0, s.latestMessageTimestamp)
						});
					}
				});

				const merged = Array.from(map.values());
				const unread = merged.filter(s => s.latestMessageTimestamp > s.lastReadTimestamp).length;
				setGlobalUnreadCount(unread);
				const sorted = merged.sort((a, b) => {
					const hasNewA = a.latestMessageTimestamp > a.lastReadTimestamp ? 1 : 0;
					const hasNewB = b.latestMessageTimestamp > b.lastReadTimestamp ? 1 : 0;
					if (hasNewA !== hasNewB) return hasNewB - hasNewA;
					return b.latestMessageTimestamp - a.latestMessageTimestamp;
				});
				setChatSummaries(sorted);
			}
		} catch (e) {
			console.error("Failed to fetch global unread count in TopNav", e);
		}
	};

	useEffect(() => {
		if (user) {
			fetchGlobalUnreadCount();
			const interval = setInterval(fetchGlobalUnreadCount, 3000);
			return () => clearInterval(interval);
		}
	}, [user]);

	const [userAttributes, setUserAttributes] = useState<FetchUserAttributesOutput>();
	const [signedPictureUrl, setSignedPictureUrl] = useState<string>();

	const [currentUserProfile, setCurrentUserProfile] = useState<{ role?: string; org?: string } | null>(null);

	useEffect(() => {
		const fetchProfile = async () => {
			if (user?.signInDetails?.loginId) {
				try {
					const data = await apiGetRequest<undefined, { role?: string; org?: string }>(`/users/${user.signInDetails.loginId}`);
					setCurrentUserProfile(data);
				} catch (e) {
					console.error("Failed to fetch user profile in TopNav", e);
				}
			}
		};
		fetchProfile();
	}, [user]);

	// Support Ticket Form States
	const { isOpen: isReportOpen, onOpen: onReportOpen, onClose: onReportClose } = useDisclosure();
	const [reportSubject, setReportSubject] = useState("");
	const [reportDescription, setReportDescription] = useState("");
	const [reportAttachment, setReportAttachment] = useState<string>();
	const [isSubmittingReport, setIsSubmittingReport] = useState(false);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onloadend = () => {
				setReportAttachment(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const handleReportSubmit = async () => {
		if (!reportSubject.trim() || !reportDescription.trim()) {
			toast({
				title: "Validation Error",
				description: "Please fill out Subject and Description.",
				status: "error",
				duration: 3000,
				isClosable: true
			});
			return;
		}
		setIsSubmittingReport(true);
		try {
			const userId = userAttributes?.sub || username || "anonymous";
			const email = userAttributes?.email || "no-email@xnav.studio";
			const name = userAttributes?.preferred_username || userAttributes?.name || username || "Anonymous User";
			const org = currentUserProfile?.org || "Global";
			
			const ticket = createTicket(
				userId,
				email,
				name,
				reportSubject,
				reportDescription,
				reportAttachment,
				org
			);

			// Trigger real email confirmation using Amazon SES backend broadcast endpoint
			try {
				await apiPostRequest('/users/email_broadcast', {
					senderEmail: "support@xnav.studio",
					subject: `[XNav Support] Ticket Raised: #${ticket.id}`,
					message: `Hello ${name},\n\nWe have received your support ticket request regarding '${reportSubject}'. Our support team has been notified and will review your request shortly.\n\nTicket ID: ${ticket.id}\nUser ID: ${userId}\nOrganization: ${org}\nSubject: ${reportSubject}\nDescription: ${reportDescription}\n\nBest regards,\nThe XNav Support Team`,
					targetAudience: "specific",
					targetValues: [email]
				});
			} catch (emailErr) {
				console.error("Amazon SES backend email transmission failed", emailErr);
			}

			toast({
				title: "Ticket Raised Successfully",
				description: `Ticket ID ${ticket.id} created. Auto-response sent to ${email}.`,
				status: "success",
				duration: 5000,
				isClosable: true
			});

			// Reset form
			setReportSubject("");
			setReportDescription("");
			setReportAttachment(undefined);
			onReportClose();
		} catch (error) {
			toast({
				title: "Error",
				description: "Failed to submit ticket. Please try again.",
				status: "error",
				duration: 3000,
				isClosable: true
			});
		} finally {
			setIsSubmittingReport(false);
		}
	};

	const [collaboratorPics, setCollaboratorPics] = useState<Record<string, string>>({});
	const [collaboratorNames, setCollaboratorNames] = useState<Record<string, string>>({});

	const collaboratorsKey = currentApp?.collaborators ? currentApp.collaborators.join(",") : "";

	useEffect(() => {
		const fetchCollaboratorProfiles = async () => {
			if (!currentApp?.collaborators || currentApp.collaborators.length === 0) {
				setCollaboratorPics({});
				setCollaboratorNames({});
				return;
			}
			
			const newPics: Record<string, string> = {};
			const newNames: Record<string, string> = {};
			
			try {
				await Promise.all(
					currentApp.collaborators.map(async (email) => {
						try {
							const userData = await apiGetRequest<undefined, { nickname?: string; picture?: string }>(
								`/users/${encodeURIComponent(email)}`
							);
							if (userData.nickname) {
								newNames[email] = userData.nickname;
							}
							if (userData.picture) {
								let mediaKeyId = userData.picture;
								if (userData.picture.includes("s3.us-west-2.amazonaws.com")) {
									const urlObj = new URL(userData.picture);
									mediaKeyId = urlObj.pathname.substring(1);
								} else if (userData.picture.startsWith("http")) {
									newPics[email] = userData.picture;
									return;
								}
								
								const picResponse = await apiGetRequest<undefined, { signedUrl: string }>(
									`/get-profile-image?mediaKeyId=${encodeURIComponent(mediaKeyId)}`
								);
								if (picResponse.signedUrl) {
									newPics[email] = picResponse.signedUrl;
								}
							}
						} catch (e) {
							console.error(`Failed to fetch profile for collaborator ${email}`, e);
						}
					})
				);
			} catch (err) {
				console.error("Error fetching collaborator profiles:", err);
			}
			
			setCollaboratorPics(newPics);
			setCollaboratorNames(newNames);
		};
		
		fetchCollaboratorProfiles();
	}, [collaboratorsKey]);

	const [userRole, setUserRole] = useState<string | null>(null);
	const [notifications, setNotifications] = useState<AppMeta[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const fetchRole = async () => {
			if (user?.signInDetails?.loginId) {
				try {
					const data = await apiGetRequest<undefined, { role: string }>(`/users/${user.signInDetails.loginId}`);
					setUserRole(data?.role || 'Instructor');
				} catch (error) {
					console.error("Failed to fetch user role for topnav", error);
					setUserRole('Instructor');
				}
			}
		};
		fetchRole();
	}, [user]);

	const fetchNotifications = async () => {
		if (!userRole || userRole === 'Instructor') return;
		setLoading(true);
		try {
			const apps = await apiGetRequest<string, AppMeta[]>('/apps');
			const pending = apps.filter(app => app.deployRequested || app.deleteRequested);
			setNotifications(pending);
		} catch (e) {
			console.error("Failed to fetch pending requests for topnav", e);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (userRole) {
			fetchNotifications();
		}
	}, [userRole]);

	const handlePopoverOpen = () => {
		onOpen();
		fetchNotifications();
	};

	useEffect(() => {
		fetchUserAttributes().then(async (attrs) => {
			setUserAttributes(attrs);
			if (attrs.picture) {
				let mediaKeyId = attrs.picture;
				if (attrs.picture.includes("s3.us-west-2.amazonaws.com")) {
					const urlObj = new URL(attrs.picture);
					mediaKeyId = urlObj.pathname.substring(1);
				} else if (attrs.picture.startsWith("http")) {
					setSignedPictureUrl(attrs.picture);
					return;
				}

				try {
					const response = await apiGetRequest<undefined, { signedUrl: string }>(`/get-profile-image?mediaKeyId=${encodeURIComponent(mediaKeyId)}`);
					if (response.signedUrl) {
						setSignedPictureUrl(response.signedUrl);
					}
				} catch (e) {
					console.error("Failed to load signed picture URL for topnav", e);
				}
			}
		}).catch(() => {});
	}, []);

	const isHeaderActionAdmin = userRole === 'Super Admin' || userRole === 'Admin' || userRole === 'Org Admin';
	const isEditorPage = pathname.endsWith('/editor') || pathname.endsWith('/editor/');

	return (
		<>
			<Flex
				as="header"
				w="100%"
				h="64px"
				bg={useColorModeValue("white", "gray.900")}
				borderBottom="1px solid"
				borderColor={useColorModeValue("gray.200", "gray.700")}
				position="sticky"
				top={0}
				zIndex={90}
				px={6}
				alignItems="center"
				justifyContent="space-between"
				gap={4}
			>
				{/* Left: Brand & Breadcrumbs */}
				<Flex align="center" gap={3.5} minW={0} flexShrink={1}>
					<Heading
						size="md"
						fontSize="lg"
						bgGradient="linear(to-r, #38bdf8, #0ea5e9, #10b981)"
						bgClip="text"
						fontWeight="black"
						letterSpacing="tight"
						cursor="pointer"
						onClick={() => handleSafeNavigate("/courses")}
						whiteSpace="nowrap"
					>
						Course Studio
					</Heading>
					<Box h="22px" w="1px" bg={useColorModeValue("gray.200", "gray.700")} />
					{(() => {
						if (appId && currentApp) {
							return (
								<HStack spacing={2} fontSize="sm" fontWeight="semibold" minW={0} overflow="hidden">
									<Tooltip label="All Courses" placement="bottom" hasArrow openDelay={250}>
										<Text 
											color="gray.400" 
											cursor="pointer" 
											onClick={() => handleSafeNavigate("/courses")} 
											_hover={{ color: "teal.500" }} 
											whiteSpace="nowrap"
										>
											My Courses
										</Text>
									</Tooltip>
									<Text color="gray.300" fontWeight="normal">/</Text>
									
									<Tooltip label={currentApp.name} placement="bottom" hasArrow openDelay={200}>
										<Text 
											color={selectionPath.length > 0 ? "gray.400" : useColorModeValue("gray.850", "white")} 
											fontWeight={selectionPath.length === 0 ? "bold" : "semibold"}
											isTruncated 
											maxW={{ base: "140px", md: "200px", xl: "280px" }}
											cursor="pointer"
											onClick={() => {
												if (selectionPath.length > 0) {
													window.dispatchEvent(new CustomEvent('editor-clear-selection'));
												}
											}}
											_hover={selectionPath.length > 0 ? { color: "teal.500" } : {}}
										>
											{currentApp.name}
										</Text>
									</Tooltip>

									{selectionPath.length <= 2 ? (
										selectionPath.map((segment, idx) => (
											<HStack key={idx} spacing={2} align="center" minW={0} overflow="hidden">
												<Text color="gray.300" fontWeight="normal">/</Text>
												<Tooltip label={segment} placement="bottom" hasArrow openDelay={150}>
													<Text 
														color={idx === selectionPath.length - 1 ? useColorModeValue("gray.850", "white") : "gray.400"} 
														fontWeight={idx === selectionPath.length - 1 ? "bold" : "normal"} 
														isTruncated 
														maxW={{ base: "140px", md: "200px", xl: "300px" }}
														cursor="default"
													>
														{segment}
													</Text>
												</Tooltip>
											</HStack>
										))
									) : (
										<>
											{/* First parent folder */}
											<HStack spacing={2} align="center" minW={0} overflow="hidden">
												<Text color="gray.300" fontWeight="normal">/</Text>
												<Tooltip label={selectionPath[0]} placement="bottom" hasArrow openDelay={150}>
													<Text 
														color="gray.400" 
														fontWeight="normal" 
														isTruncated 
														maxW={{ base: "100px", md: "160px" }}
														cursor="default"
													>
														{selectionPath[0]}
													</Text>
												</Tooltip>
											</HStack>

											{/* Collapsed middle folders */}
											<HStack spacing={2} align="center" minW={0} flexShrink={0}>
												<Text color="gray.300" fontWeight="normal">/</Text>
												<Tooltip 
													label={
														<VStack align="start" spacing={1} p={1}>
															<Text fontSize="2xs" color="gray.300" textTransform="uppercase" fontWeight="bold">Path hierarchy:</Text>
															{selectionPath.slice(1, -1).map((mid, midIdx) => (
																<Text key={midIdx} fontSize="xs" fontWeight="semibold">› {mid}</Text>
															))}
														</VStack>
													} 
													placement="bottom" 
													hasArrow 
													openDelay={100}
												>
													<Box 
														px={2} 
														py={0.5} 
														borderRadius="md" 
														bg={useColorModeValue("gray.100", "gray.800")} 
														color={useColorModeValue("gray.600", "gray.300")}
														fontSize="xs"
														fontWeight="bold"
														cursor="pointer"
														_hover={{ bg: useColorModeValue("gray.200", "gray.700") }}
													>
														•••
													</Box>
												</Tooltip>
											</HStack>

											{/* Last active leaf element */}
											<HStack spacing={2} align="center" minW={0} overflow="hidden">
												<Text color="gray.300" fontWeight="normal">/</Text>
												<Tooltip label={selectionPath[selectionPath.length - 1]} placement="bottom" hasArrow openDelay={150}>
													<Text 
														color={useColorModeValue("gray.850", "white")} 
														fontWeight="bold" 
														isTruncated 
														maxW={{ base: "140px", md: "240px", xl: "360px" }}
														cursor="default"
													>
														{selectionPath[selectionPath.length - 1]}
													</Text>
												</Tooltip>
											</HStack>
										</>
									)}
								</HStack>
							);
						}

						// Non-course page breadcrumbs
						let pageTitle = "My Courses";
						if (pathname === "/") {
							pageTitle = "Dashboard";
						} else if (pathname === "/courses") {
							pageTitle = "My Courses";
						} else if (pathname.startsWith("/media-hub")) {
							pageTitle = "Global Asset Hub";
						} else if (pathname.startsWith("/question-bank")) {
							pageTitle = "Master Question Bank";
						} else if (pathname.startsWith("/quizzes")) {
							pageTitle = "Quiz Engine";
						} else if (pathname.startsWith("/flashcards")) {
							pageTitle = "Flashcard Engine";
						} else if (pathname.startsWith("/users")) {
							pageTitle = "User Management";
						} else if (pathname.startsWith("/broadcasts")) {
							pageTitle = "Broadcasts";
						} else if (pathname.startsWith("/mobile-banner")) {
							pageTitle = "Mobile Banner";
						} else if (pathname.startsWith("/logs")) {
							pageTitle = "Global Logs";
						}

						return (
							<HStack spacing={2} fontSize="sm" fontWeight="bold">
								<Text fontSize="md" color={useColorModeValue("gray.800", "white")}>{pageTitle}</Text>
							</HStack>
						);
					})()}
				</Flex>

				{/* Center Quick Tools (when in editor page) */}
				{isEditorPage && (
					<HStack spacing={1.5} bg={useColorModeValue("gray.100", "gray.800")} p={1} borderRadius="full" boxShadow="inner">
						<Button
							size="sm"
							variant="ghost"
							colorScheme="teal"
							leftIcon={<FontAwesomeIcon icon={faVideo} />}
							onClick={() => handleSafeNavigate(`/${appId}/media`)}
							borderRadius="full"
							fontSize="xs"
							fontWeight="600"
							h="34px"
							px={4}
							_hover={{ bg: useColorModeValue("white", "gray.700"), shadow: "xs" }}
						>
							Media Manager
						</Button>
						<Button
							size="sm"
							variant="ghost"
							colorScheme="teal"
							leftIcon={<FontAwesomeIcon icon={faQuestionCircle} />}
							onClick={() => window.dispatchEvent(new CustomEvent('editor-quiz'))}
							borderRadius="full"
							fontSize="xs"
							fontWeight="600"
							h="34px"
							px={4}
							_hover={{ bg: useColorModeValue("white", "gray.700"), shadow: "xs" }}
						>
							Quiz
						</Button>
						<Button
							size="sm"
							variant="ghost"
							colorScheme="teal"
							leftIcon={<FontAwesomeIcon icon={faBolt} />}
							onClick={() => window.dispatchEvent(new CustomEvent('editor-flashcards'))}
							borderRadius="full"
							fontSize="xs"
							fontWeight="600"
							h="34px"
							px={4}
							_hover={{ bg: useColorModeValue("white", "gray.700"), shadow: "xs" }}
						>
							Flashcards
						</Button>
					</HStack>
				)}

				<Flex alignItems="center" gap={3} flexShrink={0}>
					{/* Save & Cancel when in editor */}
					{isEditorPage && (
						<HStack spacing={2} mr={1}>
							<Button
								size="sm"
								onClick={() => window.dispatchEvent(new CustomEvent('editor-save'))}
								leftIcon={<FontAwesomeIcon icon={isDirty ? faSave : faCheck} />}
								colorScheme={isDirty ? "brand" : "gray"}
								bg={isDirty ? "linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)" : undefined}
								color={isDirty ? "white" : undefined}
								variant={isDirty ? "solid" : "outline"}
								isDisabled={!isDirty || isSaving}
								isLoading={isSaving}
								loadingText="Saving..."
								borderRadius="full"
								px={4.5}
								h="34px"
								fontSize="xs"
								fontWeight="600"
								shadow={isDirty ? "0 2px 8px rgba(14, 165, 233, 0.4)" : "none"}
								_hover={isDirty ? { bg: "linear-gradient(135deg, #0284c7 0%, #059669 100%)" } : {}}
							>
								{isDirty ? "Save Changes" : "Saved"}
							</Button>
							<Button
								size="sm"
								onClick={() => window.dispatchEvent(new CustomEvent('editor-cancel'))}
								leftIcon={<FontAwesomeIcon icon={faTimes} />}
								colorScheme="red"
								variant="outline"
								isDisabled={!isDirty || isSaving}
								borderRadius="full"
								px={4}
								h="34px"
								fontSize="xs"
								fontWeight="600"
							>
								Cancel
							</Button>
						</HStack>
					)}

					{currentApp?.collaborators && currentApp.collaborators.length > 0 && (
						<Menu isLazy>
							<MenuButton
								as={Button}
								size="sm"
								variant="ghost"
								borderRadius="full"
								px={3.5}
								h="34px"
								leftIcon={<FontAwesomeIcon icon={faUsers} color="#0ea5e9" />}
								_hover={{ bg: useColorModeValue("cyan.50", "rgba(14, 165, 233, 0.15)") }}
							>
								<HStack spacing={1.5}>
									<Text fontSize="xs" fontWeight="semibold" color={useColorModeValue("gray.700", "gray.200")}>
										Collaborators
									</Text>
									<Badge colorScheme="cyan" borderRadius="full" fontSize="10px" px={1.5}>
										{currentApp.collaborators.length}
									</Badge>
								</HStack>
							</MenuButton>
							<Portal>
								<MenuList
									shadow="2xl"
									borderRadius="2xl"
									border="none"
									bg={useColorModeValue("white", "gray.850")}
									py={2}
									minW="220px"
									zIndex={999}
								>
									<Box px={4} py={2} borderBottom="1px solid" borderColor={useColorModeValue("gray.100", "gray.750")} mb={1}>
										<Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
											Course Collaborators
										</Text>
									</Box>
									{currentApp.collaborators.map((c: string) => {
										const nickname = collaboratorNames[c] || c.split('@')[0];
										const picUrl = collaboratorPics[c] || undefined;
										return (
											<MenuItem
												key={c}
												px={4}
												py={2}
												_hover={{ bg: useColorModeValue("cyan.50", "rgba(14, 165, 233, 0.15)") }}
											>
												<HStack spacing={3} w="100%">
													<Avatar
														size="xs"
														name={nickname}
														src={picUrl}
														bg="cyan.500"
														color="white"
													/>
													<VStack align="start" spacing={0} overflow="hidden">
														<Text fontSize="sm" fontWeight="bold" color={useColorModeValue("gray.800", "gray.100")} isTruncated maxW="150px">
															{nickname}
														</Text>
														<Text fontSize="10px" color="gray.400" isTruncated maxW="150px">
															{c}
														</Text>
													</VStack>
												</HStack>
											</MenuItem>
										);
									})}
								</MenuList>
							</Portal>
						</Menu>
					)}
					<IconButton
						aria-label="Toggle Color Mode"
						icon={<FontAwesomeIcon icon={colorMode === "light" ? faSun : faMoon} />}
						onClick={toggleColorMode}
						variant="ghost"
						size="sm"
						h="34px"
						w="34px"
						borderRadius="full"
					/>

					{isHeaderActionAdmin ? (
						<Popover isOpen={isOpen} onOpen={handlePopoverOpen} onClose={onClose} placement="bottom-end" closeOnBlur={true}>
							<PopoverTrigger>
								<Box position="relative" cursor="pointer">
									<IconButton
										aria-label="Notifications"
										icon={<FontAwesomeIcon icon={faBell} />}
										variant="ghost"
										size="sm"
										borderRadius="full"
									/>
									{notifications.length > 0 && (
										<Badge
											position="absolute"
											top="-1px"
											right="-1px"
											colorScheme="red"
											borderRadius="full"
											px={1.5}
											py={0.5}
											fontSize="10px"
											fontWeight="bold"
											shadow="md"
										>
											{notifications.length}
										</Badge>
									)}
								</Box>
							</PopoverTrigger>
							<Portal>
								<PopoverContent
									w="400px"
									bg={useColorModeValue("rgba(255, 255, 255, 0.85)", "rgba(20, 16, 35, 0.85)")}
									backdropFilter="blur(20px)"
									border="1px solid"
									borderColor={useColorModeValue("purple.100/60", "purple.900/30")}
									borderRadius="2xl"
									shadow="2xl"
									_focus={{ outline: 'none' }}
									zIndex={999}
								>
									<PopoverCloseButton
										borderRadius="full"
										size="sm"
										top={3.5}
										right={3.5}
										_hover={{ bg: useColorModeValue("purple.50", "purple.950/40"), color: "purple.500" }}
									/>
									<PopoverHeader p={4} borderBottom="1px solid" borderColor={useColorModeValue("purple.50/80", "purple.900/20")}>
										<Flex justify="space-between" align="center">
											<Heading size="xs" color="purple.500" textTransform="uppercase" letterSpacing="widest" fontWeight="extrabold">
												Pending Requests
											</Heading>
											<Badge colorScheme="purple" borderRadius="full" px={2.5} py={0.5} mr="36px" fontSize="10px" fontWeight="bold">
												{notifications.length} New
											</Badge>
										</Flex>
									</PopoverHeader>
									<PopoverBody p={0} maxH="400px" overflowY="auto">
										{loading ? (
											<Flex p={8} justify="center"><Spinner size="md" color="purple.500" /></Flex>
										) : notifications.length === 0 ? (
											<Flex p={8} direction="column" align="center" justify="center" gap={2}>
												<Text fontSize="sm" color="gray.500" fontWeight="semibold">All caught up!</Text>
												<Text fontSize="xs" color="gray.400">No pending deploy or delete requests.</Text>
											</Flex>
										) : (
											<Stack spacing={0} divider={<Divider borderColor={useColorModeValue("purple.50/80", "purple.900/20")} />}>
												{notifications.map((app) => {
													const isDeploy = app.deployRequested;

													return (
														<Box key={app.id} p={4} _hover={{ bg: useColorModeValue("purple.50/30", "purple.950/10") }} transition="all 0.2s">
															<Flex gap={3.5} align="center">
																<Avatar size="sm" name={app.name} src={app.iconUrl || '/default.png'} borderRadius="xl" />
																<Flex direction="column" gap={0.5} flex={1}>
																	<Flex justify="space-between" align="center" gap={2}>
																		<Text fontSize="sm" fontWeight="bold" color={textTitleColor} isTruncated maxW="200px">
																			{app.name}
																		</Text>
																		<Badge colorScheme={isDeploy ? "purple" : "red"} variant="solid" borderRadius="full" px={2} py={0.5} fontSize="9px">
																			{isDeploy ? "Deploy" : "Delete"}
																		</Badge>
																	</Flex>
																	<Text fontSize="xs" color="gray.500">
																		{isDeploy ? (
																			<span>Requested deployment by <b>{app.creator || 'Instructor'}</b></span>
																		) : (
																			<span>Requested deletion by <b>{app.creator || 'Instructor'}</b></span>
																		)}
																	</Text>
																	<Text fontSize="10px" color="gray.400">
																		{new Date(app.timestamp).toLocaleString()}
																	</Text>
																</Flex>
															</Flex>
														</Box>
													);
												})}
											</Stack>
										)}
									</PopoverBody>
								</PopoverContent>
							</Portal>
						</Popover>
					) : (
						<IconButton
							aria-label="Notifications"
							icon={<FontAwesomeIcon icon={faBell} />}
							variant="ghost"
							size="sm"
							borderRadius="full"
							isDisabled={true}
						/>
					)}

					<Popover isOpen={isRecentChatsOpen} onOpen={onRecentChatsOpen} onClose={onRecentChatsClose} placement="bottom-end">
						<PopoverTrigger>
							<Box position="relative" cursor="pointer">
								<IconButton
									aria-label="Chat"
									icon={<FontAwesomeIcon icon={faComments} />}
									variant="ghost"
									size="sm"
									borderRadius="full"
								/>
								{globalUnreadCount > 0 && (
									<Badge
										position="absolute"
										top="-1px"
										right="-1px"
										colorScheme="red"
										borderRadius="full"
										px={1.5}
										py={0.5}
										fontSize="10px"
										fontWeight="bold"
										shadow="md"
									>
										{globalUnreadCount}
									</Badge>
								)}
							</Box>
						</PopoverTrigger>
						<Portal>
							<PopoverContent
								w="320px"
								bg={useColorModeValue("rgba(255, 255, 255, 0.95)", "rgba(20, 16, 35, 0.95)")}
								backdropFilter="blur(20px)"
								border="1px solid"
								borderColor={useColorModeValue("teal.100/60", "teal.900/30")}
								borderRadius="2xl"
								shadow="2xl"
								_focus={{ outline: 'none' }}
								zIndex={999}
							>
								<PopoverCloseButton
									borderRadius="full"
									size="sm"
									top={3.5}
									right={3.5}
									_hover={{ bg: useColorModeValue("teal.50", "teal.950/40"), color: "teal.500" }}
								/>
								<PopoverHeader p={4} borderBottom="1px solid" borderColor={useColorModeValue("teal.50/80", "teal.900/20")}>
									<Flex justify="space-between" align="center">
										<Heading size="xs" color="teal.500" textTransform="uppercase" letterSpacing="widest" fontWeight="extrabold">
											Recent Chats
										</Heading>
										{globalUnreadCount > 0 && (
											<Badge colorScheme="red" borderRadius="full" px={2} py={0.5} mr="36px" fontSize="9px" fontWeight="bold">
												{globalUnreadCount} New
											</Badge>
										)}
									</Flex>
								</PopoverHeader>
								<PopoverBody p={0} maxH="300px" overflowY="auto">
									{chatSummaries.length === 0 ? (
										<Flex p={6} direction="column" align="center" justify="center" gap={1}>
											<Text fontSize="xs" color="gray.500" fontWeight="semibold">No conversations yet.</Text>
											<Text fontSize="10px" color="gray.400">Announcements and messages will show up here.</Text>
										</Flex>
									) : (
										<Stack spacing={0} divider={<Divider borderColor={useColorModeValue("teal.50/80", "teal.900/20")} />}>
											{chatSummaries.map((s) => {
												const parsed = parseChatId(s.chatId, user?.signInDetails?.loginId || '');
												const hasNew = s.latestMessageTimestamp > s.lastReadTimestamp;
												const otherUserEmail = parsed.otherEmail || '';
												
												let avatarSrc = undefined;
												if (!parsed.isBroadcast && otherUserEmail) {
													avatarSrc = `https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(otherUserEmail)}`;
												}

												return (
													<Box
														key={s.chatId}
														p={3}
														_hover={{ bg: useColorModeValue("teal.50/30", "teal.950/10") }}
														transition="all 0.2s"
														cursor="pointer"
														onClick={() => {
															setSelectedChatId(s.chatId);
															setSelectedChatTitle(parsed.title);
															onRecentChatsClose();
															onChatOpen();
														}}
													>
														<Flex gap={3} align="center">
															<Avatar
																size="xs"
																name={parsed.title}
																src={avatarSrc}
																borderRadius="md"
															/>
															<Flex direction="column" flex={1} overflow="hidden">
																<Flex align="center" justify="space-between">
																	<Text fontSize="xs" fontWeight="bold" isTruncated color={useColorModeValue("gray.800", "gray.200")}>
																		{parsed.title}
																	</Text>
																	{hasNew && (
																		<Badge colorScheme="red" fontSize="8px" borderRadius="full" px={1.5}>NEW</Badge>
																	)}
																</Flex>
																<Text fontSize="9px" color="gray.500" isTruncated>
																	{parsed.subtitle}
																</Text>
															</Flex>
														</Flex>
													</Box>
												);
											})}
										</Stack>
									)}
								</PopoverBody>
								<Flex p={3} borderTop="1px solid" borderColor={useColorModeValue("teal.50/80", "teal.900/20")} justify="center">
									<Button
										size="xs"
										colorScheme="teal"
										variant="ghost"
										w="100%"
										borderRadius="xl"
										onClick={() => {
											onRecentChatsClose();
											onChatOpen();
										}}
									>
										Open Full Chat Drawer
									</Button>
								</Flex>
							</PopoverContent>
						</Portal>
					</Popover>

					<Menu>
						<MenuButton as={Button} rounded="full" variant="link" cursor="pointer" minW={0}>
							<Avatar size="sm" name={userAttributes?.preferred_username || username} src={signedPictureUrl} />
						</MenuButton>
						<MenuList>
							<MenuItem onClick={() => handleSafeNavigate("/profile")}>Profile</MenuItem>
							<MenuItem onClick={onReportOpen}>Report Issue</MenuItem>
							<MenuItem onClick={signOut}>Sign out</MenuItem>
						</MenuList>
					</Menu>
				</Flex>
			</Flex>

			<TopNavChat 
				isOpen={isChatOpen} 
				onClose={() => {
					onChatClose();
					setSelectedChatId('');
					setSelectedChatTitle('');
				}} 
				initialChatId={selectedChatId} 
				initialChatTitle={selectedChatTitle} 
			/>

			{/* Support Ticket Report Modal */}
			<Modal isOpen={isReportOpen} onClose={onReportClose} size="lg" isCentered>
				<ModalOverlay backdropFilter="blur(8px)" bg="rgba(15, 23, 42, 0.4)" />
				<ModalContent borderRadius="2xl" border="1px solid" borderColor={useColorModeValue("purple.50", "gray.750")} bg={useColorModeValue("white", "gray.850")} shadow="2xl" p={2}>
					<ModalHeader display="flex" alignItems="center" gap={3} fontWeight="bold" borderBottom="1px solid" borderColor={useColorModeValue("gray.50", "gray.750")} color={useColorModeValue("green.600", "green.300")}>
						<Box display="flex" alignItems="center" justifyContent="center" boxSize="36px" borderRadius="full" bg={useColorModeValue("green.50", "rgba(16, 185, 129, 0.15)")} color="green.500">
							<FontAwesomeIcon icon={faBug} />
						</Box>
						Report an Issue / Give Feedback
					</ModalHeader>
					<ModalCloseButton borderRadius="full" m={2} />
					<ModalBody py={6}>
						<VStack spacing={4} align="stretch">
							<SimpleGrid columns={2} spacing={3}>
								<FormControl isReadOnly>
									<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Name</FormLabel>
									<Input size="sm" borderRadius="lg" bg={useColorModeValue("gray.50", "gray.900/50")} value={userAttributes?.preferred_username || userAttributes?.name || username || "Anonymous"} />
								</FormControl>
								<FormControl isReadOnly>
									<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Email</FormLabel>
									<Input size="sm" borderRadius="lg" bg={useColorModeValue("gray.50", "gray.900/50")} value={userAttributes?.email || "no-email@xnav.studio"} />
								</FormControl>
							</SimpleGrid>

							<SimpleGrid columns={2} spacing={3}>
								<FormControl isReadOnly>
									<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">User ID</FormLabel>
									<Input size="sm" borderRadius="lg" bg={useColorModeValue("gray.50", "gray.900/50")} value={userAttributes?.sub || username || "anonymous"} />
								</FormControl>
								<FormControl isReadOnly>
									<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Organization</FormLabel>
									<Input size="sm" borderRadius="lg" bg={useColorModeValue("gray.50", "gray.900/50")} value={currentUserProfile?.org === "Global" || !currentUserProfile?.org ? "XNav" : currentUserProfile.org} />
								</FormControl>
							</SimpleGrid>

							<FormControl isRequired>
								<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Subject</FormLabel>
								<Input size="sm" borderRadius="lg" placeholder="Short description of the problem..." value={reportSubject} onChange={(e) => setReportSubject(e.target.value)} />
							</FormControl>

							<FormControl isRequired>
								<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Description</FormLabel>
								<Textarea size="sm" borderRadius="lg" rows={4} placeholder="Describe the steps to reproduce the issue or details of your request..." value={reportDescription} onChange={(e) => setReportDescription(e.target.value)} />
							</FormControl>

							<FormControl>
								<FormLabel fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">Attachment (Image)</FormLabel>
								<Input type="file" accept="image/*" size="sm" p={1} borderRadius="lg" onChange={handleFileChange} />
								{reportAttachment && (
									<Box mt={2} maxH="120px" overflow="hidden" borderRadius="lg" border="1px solid" borderColor="gray.200">
										<img src={reportAttachment} alt="Preview" style={{ maxHeight: "120px", objectFit: "contain" }} />
									</Box>
								)}
							</FormControl>
						</VStack>
					</ModalBody>
					<ModalFooter borderTop="1px solid" borderColor={useColorModeValue("gray.50", "gray.750")} gap={3}>
						<Button size="sm" borderRadius="full" variant="ghost" onClick={onReportClose}>Cancel</Button>
						<Button size="sm" borderRadius="full" colorScheme="green" px={5} isLoading={isSubmittingReport} onClick={handleReportSubmit}>Submit Report</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Unsaved Changes Confirmation Modal */}
			<Modal isOpen={showUnsavedModal} onClose={() => setShowUnsavedModal(false)} isCentered size="md">
				<ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.600" />
				<ModalContent borderRadius="2xl" shadow="2xl" border="1px solid" borderColor={useColorModeValue("gray.100", "gray.700")}>
					<ModalHeader pb={1} pt={5}>
						<HStack spacing={3}>
							<Flex w="36px" h="36px" borderRadius="full" bg="orange.50" _dark={{ bg: "orange.900" }} align="center" justify="center" color="orange.400">
								<FontAwesomeIcon icon={faExclamationTriangle} />
							</Flex>
							<Text fontSize="lg" fontWeight="bold">Unsaved Changes</Text>
						</HStack>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody py={4}>
						<Text fontSize="sm" color={useColorModeValue("gray.600", "gray.300")}>
							You have unsaved changes in this course. If you leave now, any new annotations, hot spots, clipping bounds, or outline changes will be lost.
						</Text>
					</ModalBody>
					<ModalFooter gap={2} borderTop="1px solid" borderColor={useColorModeValue("gray.100", "gray.750")} pt={3} pb={4}>
						<Button
							size="sm"
							variant="ghost"
							colorScheme="red"
							borderRadius="full"
							px={4}
							onClick={() => {
								setShowUnsavedModal(false);
								setIsDirty(false);
								window.dispatchEvent(new CustomEvent('editor-cancel'));
								if (pendingNavPath) {
									const target = pendingNavPath;
									setPendingNavPath(null);
									navigate(target);
								}
							}}
						>
							Discard Changes
						</Button>
						<Button
							size="sm"
							variant="outline"
							borderRadius="full"
							px={4}
							onClick={() => {
								setShowUnsavedModal(false);
								setPendingNavPath(null);
							}}
						>
							Stay on Page
						</Button>
						<Button
							size="sm"
							colorScheme="teal"
							borderRadius="full"
							px={4.5}
							leftIcon={<FontAwesomeIcon icon={faSave} />}
							onClick={() => {
								window.dispatchEvent(new CustomEvent('editor-save'));
								setShowUnsavedModal(false);
								if (pendingNavPath) {
									const target = pendingNavPath;
									setPendingNavPath(null);
									const onClean = (e: Event) => {
										const ce = e as CustomEvent<{ isDirty: boolean }>;
										if (!ce.detail?.isDirty) {
											window.removeEventListener('editor-dirty-state', onClean);
											navigate(target);
										}
									};
									window.addEventListener('editor-dirty-state', onClean);
									setTimeout(() => {
										window.removeEventListener('editor-dirty-state', onClean);
										navigate(target);
									}, 1200);
								}
							}}
						>
							Save & Continue
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
}
